import Service from '@/pages/dashboard/components/PageContainer/Service';
import React from 'react';

/** AiSummaryBridge props：Bridge 负责把父级透传 props 注入给 cloneElement 的子节点 */
interface IAiSummaryBridgeProps {
  children?: React.ReactNode;
  eventData?: unknown;
  anomalyResult?: unknown;
  anomalyResultError?: unknown;
  anomalyResultLoading?: unknown;
  aiEnabled?: boolean;
  // 其他 props 透传；由 Service 在运行时注入（data/filterLoading 等）
  [key: string]: unknown;
}

/**
 * Bridge：Service 只向下传递 data + filterLoading，
 * 这里把 Service 提供的 data 与外层捕获的 eventData / anomalyResult 合并后再传给 children。
 */
function AiSummaryBridge(props: IAiSummaryBridgeProps) {
  const {
    children,
    eventData,
    anomalyResult,
    anomalyResultError,
    anomalyResultLoading,
    aiEnabled,
    ...rest
  } = props;

  return (
    <>
      {React.Children.map(children, (child) => {
        // 非有效 React 元素（文本节点、null 等）原样返回
        if (!React.isValidElement(child)) {
          return child;
        }
        // 注入的 props 形态开放，使用 React.Attributes & Record 保证 cloneElement 类型签名通过
        const injectedProps: React.Attributes & Record<string, unknown> = {
          ...rest,
          eventData,
          anomalyResult,
          anomalyResultError,
          anomalyResultLoading,
          aiEnabled,
        };
        return React.cloneElement(child, injectedProps);
      })}
    </>
  );
}

/** KpiAiSummaryService props：在 Service(ai_summary) 外层封装的薄组件 */
interface IKpiAiSummaryServiceProps {
  children?: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
  eventData?: unknown;
  anomalyResult?: unknown;
  anomalyResultError?: unknown;
  anomalyResultLoading?: unknown;
  api?: string | ((data: unknown, outParame?: unknown) => void);
  dataKey?: string;
  trackData?: string[];
  // beforeRequest/formatData 来自上游注册表配置，形态由父级决定
  beforeRequest?: string | ((data: unknown) => unknown);
  formatData?: string | ((data: unknown, filterVal?: unknown) => unknown);
  data?: Record<string, unknown>;
  /** AI 模块总开关，设为 false 时不调 AI 接口、不渲染 AI 卡片，其他子组件正常 */
  enabled?: boolean;
}

/**
 * 在 Service(ai_summary) 外层截获 eventData / anomalyResult 等透传 props，
 * 通过 AiSummaryBridge 在 Service 内部重新注入给子组件，
 * 避免修改底层 Service 组件。
 */
export default function KpiAiSummaryService(props: IKpiAiSummaryServiceProps) {
  const {
    children,
    style,
    className,
    eventData,
    anomalyResult,
    anomalyResultError,
    anomalyResultLoading,
    api,
    dataKey = 'ai_summary',
    trackData,
    beforeRequest,
    formatData,
    data,
    // AI 模块总开关，设为 false 时不调 AI 接口、不渲染 AI 卡片，其他子组件正常
    enabled = true,
  } = props;

  const bridgeProps = {
    eventData,
    anomalyResult,
    anomalyResultError,
    anomalyResultLoading,
    aiEnabled: enabled,
  };

  // enabled=false 时跳过 Service（不发 AI 接口），直接渲染子组件
  if (!enabled) {
    return (
      <div className={className} style={style}>
        <AiSummaryBridge {...bridgeProps}>
          {children}
        </AiSummaryBridge>
      </div>
    );
  }

  return (
    <div className={className} style={style}>
      <Service
        api={api}
        data={data}
        dataKey={dataKey}
        trackData={trackData}
        beforeRequest={beforeRequest}
        formatData={formatData}
      >
        <AiSummaryBridge {...bridgeProps}>
          {children}
        </AiSummaryBridge>
      </Service>
    </div>
  );
}
